
import { useState, useEffect } from 'react';
import MobileDetect from 'mobile-detect';

interface MobileDetectResult {
  isMobile: () => boolean;
  isTablet: () => boolean;
  isPhone: () => boolean;
  isMobileOrTablet: () => boolean;
  userAgent: string | null;
}

export function useMobileDetect(): MobileDetectResult {
  const [md, setMd] = useState<MobileDetect | null>(null);

  useEffect(() => {
    // Only run on client side
    if (typeof window !== 'undefined') {
      const mobileDetect = new MobileDetect(window.navigator.userAgent);
      setMd(mobileDetect);
    }
  }, []);

  // Chrome on iPad detection helper
  const isChromeOnTablet = (): boolean => {
    if (!md) return false;
    
    // Check if it's Chrome browser with tablet features
    const isChrome = md.is('Chrome');
    const hasTouch = typeof window !== 'undefined' && (
      'ontouchstart' in window || 
      navigator.maxTouchPoints > 0
    );
    
    // Chrome on iPad often reports as desktop, so check for touch + reasonable screen size
    if (isChrome && hasTouch && typeof window !== 'undefined') {
      const screenWidth = window.screen.width;
      const screenHeight = window.screen.height;
      const maxDimension = Math.max(screenWidth, screenHeight);
      
      // Tablet-like screen dimensions with touch
      return maxDimension >= 768 && maxDimension <= 1366;
    }
    
    return false;
  };

  return {
    isMobile: () => md?.mobile() !== null,
    isTablet: () => {
      // Use mobile-detect's native tablet detection + Chrome on tablet fallback
      const nativeTablet = md?.tablet() !== null;
      const chromeTablet = isChromeOnTablet();
      
      return nativeTablet || chromeTablet;
    },
    isPhone: () => md?.phone() !== null,
    isMobileOrTablet: () => {
      // Combine all mobile/tablet detection methods
      const nativeMobileOrTablet = md?.mobile() !== null || md?.tablet() !== null;
      const chromeTablet = isChromeOnTablet();
      
      return nativeMobileOrTablet || chromeTablet;
    },
    userAgent: md?.userAgent() || null,
  };
}
